import React, { useCallback, useState } from 'react';

import AcUnitIcon from '@mui/icons-material/AcUnit';
import AddIcon from '@mui/icons-material/Add';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LabelWithIcon from 'client/app/apps/disco-prototype/components/LabelWithIcon';
import LiquidList from 'client/app/apps/disco-prototype/components/LiquidList';
import LowProfileButton from 'client/app/apps/disco-prototype/components/LowProfileButton';
import { PlateMapCanvas } from 'client/app/apps/disco-prototype/components/PlateMapCanvas';
import { CheckboxMixer } from 'client/app/apps/disco-prototype/components/PlateMapCanvas/coloring/CheckboxMixer';
import { PlateMapTooltip } from 'client/app/apps/disco-prototype/components/PlateMapCanvas/PlateMapTooltip';
import Grid2x2 from 'client/app/apps/disco-prototype/components/Steps/Grid2x2';
import {
  StepInput,
  StepLabelWithIcon,
  StepSelect,
  StepSelector,
} from 'client/app/apps/disco-prototype/components/Steps/StepSelector';
import { discoPrototypeRoutes } from 'client/app/apps/disco-prototype/DiscoPrototypeRouter';
import {
  Action,
  ActionType,
} from 'client/app/apps/disco-prototype/state/actions/actions';
import { IncubateActionUpdate } from 'client/app/apps/disco-prototype/state/actions/incubateAction';
import { MakeMixtureAction } from 'client/app/apps/disco-prototype/state/actions/makeMixtureAction';
import { LiquidSet } from 'client/app/apps/disco-prototype/state/liquidSet';
import { useNavigation } from 'common/ui/components/navigation/useNavigation';

const TEAL_COLOR = '#4CC3B6'; // TODO: FIXME we should add this color to theme

const colors = [
  '#D73027', // Strong Red
  '#FC8D59', // Coral/Orange
  '#FEE08B', // Light Yellow
  '#91BFDB', // Light Blue
  '#4575B4', // Deep Blue
  '#E7298A', // Pink/Magenta
  '#66A61E', // Olive Green
  '#A6761D', // Mustard
  '#1B9E77', // Teal
  '#7570B3', // Soft Purple
  '#E6AB02', // Yellow Ochre
  '#A6CEE3', // Pale Blue
  '#B2DF8A', // Pale Green
  '#FDBF6F', // Pale Orange
  '#CAB2D6', // Lilac
  '#FF7F00', // Orange
  '#6A3D9A', // Deep Purple
  '#B15928', // Brown
  '#F781BF', // Light Pink
  '#999999', // Neutral Gray
];

const ActionTypeToIcon = {
  makeMixture: WaterDropIcon,
  incubate: AcUnitIcon,
};

type Props = {
  stepId: string;
  target: LiquidSet | undefined;
  actions: Action[];
  liquidSets: LiquidSet[];
  handleUpdateStep: (id: string, update: Record<string, any>) => void;
};

const ActionsList: React.FC<Props> = ({
  stepId,
  target,
  actions,
  liquidSets,
  handleUpdateStep,
}) => {
  const { navigate } = useNavigation();
  const [newActionType, setNewActionType] = useState<ActionType>('makeMixture');

  const plate = target?.plate;

  const getWeightNamesAndLabels = useCallback(
    (id: string) => {
      const action = actions.find(action => action.id === id) as MakeMixtureAction;
      const labels = action.additions.map(addition => addition.sourceName);
      const weightNames = action.additions.reduce<string[]>(
        (acc, value) => [...acc, value.compiled?.volume.name!],
        [],
      );

      return { weightNames, labels };
    },
    [actions],
  );

  const handleAddAction = () => {
    handleUpdateStep(stepId, { type: 'createAction', actionType: newActionType });
  };

  const handleUpdateLiquidSet = (id: string, type: ActionType, source: string) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: id,
      update: { type, source },
    });
  };

  const handleUpdateIncubate = (id: string, update: Partial<IncubateActionUpdate>) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: id,
      update: { type: 'incubate', ...update },
    });
  };

  const handleDelete = (id: string) => {
    handleUpdateStep(stepId, { type: 'deleteAction', toDelete: id });
  };

  const handleOnNavigate = (actionId: string) => {
    navigate(discoPrototypeRoutes.makeMixture, {
      stepId,
      actionId,
    });
  };

  return (
    <Stack spacing={8}>
      {actions.map(action => (
        <Stack spacing={4} key={action.id} mb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box sx={{ alignSelf: 'start' }}>
              <StepSelector
                headerColor={TEAL_COLOR}
                Header={
                  <Typography
                    variant="h6"
                    alignSelf="center"
                    sx={{ fontWeight: 'bold', color: 'text.primary' }}
                  >
                    Action
                  </Typography>
                }
                Body={
                  <StepLabelWithIcon
                    title={action.title}
                    icon={ActionTypeToIcon[action.type]}
                    sx={{ minWidth: '200px' }}
                  />
                }
              />
            </Box>

            <Stack direction="row" spacing={2}>
              <IconButton
                size="small"
                aria-label="Delete action"
                onClick={() => handleDelete(action.id)}
                sx={theme => ({
                  width: 32,
                  height: 32,
                  backgroundColor: TEAL_COLOR,
                  color: theme.palette.common.black,
                  p: 0,
                  borderRadius: theme.spacing(2),
                  '&:hover': {
                    backgroundColor: TEAL_COLOR,
                    opacity: 0.7,
                  },
                })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Box mt={1} ml={2}>
            {action.type === 'makeMixture' ? (
              <Stack spacing={4}>
                <Box sx={{ alignSelf: 'start' }}>
                  <StepSelector
                    headerColor={TEAL_COLOR}
                    Header={
                      <Typography
                        variant="h6"
                        alignSelf="center"
                        sx={{ fontWeight: 'bold', color: 'text.primary' }}
                      >
                        Source liquids
                      </Typography>
                    }
                    Body={
                      <StepSelect
                        value={action.source?.id || ''}
                        label="Select liquid set"
                        options={liquidSets.map(si => ({
                          value: `${si.id}`,
                          label: si.name,
                        }))}
                        onChange={e => {
                          handleUpdateLiquidSet(action.id, 'makeMixture', e.target.value);
                        }}
                        sx={{ minWidth: '200px' }}
                      />
                    }
                  />
                </Box>
                <Grid2x2
                  topLeft={
                    <LabelWithIcon
                      icon={<ShuffleIcon />}
                      text={`Mixtures: ${target?.table.rows.length || 0}`}
                      onEdit={() => !!action.source?.id && handleOnNavigate(action.id)}
                    />
                  }
                  topRight={
                    <LabelWithIcon icon={<CalendarViewMonthIcon />} text="Plates" />
                  }
                  bottomLeft={
                    action.additions.length > 0 ? (
                      <LiquidList items={action.additions} />
                    ) : (
                      <Typography variant="body1">Undefined</Typography>
                    )
                  }
                  bottomRight={
                    <CheckboxMixer
                      location={target?.liquidProps.well.selectedColumnName!}
                      colors={colors}
                      weightNames={getWeightNamesAndLabels(action.id).weightNames}
                      labels={getWeightNamesAndLabels(action.id).labels}
                      data={target?.table.compiled}
                      render={wells => (
                        <PlateMapTooltip
                          wells={wells}
                          location={target?.liquidProps.well.selectedColumnName!}
                          data={target?.table.compiled}
                          weightNames={getWeightNamesAndLabels(action.id).weightNames}
                          labels={getWeightNamesAndLabels(action.id).labels}
                        >
                          {({ onWellEnter, onWellLeave }) => (
                            <PlateMapCanvas
                              showHeaders={false}
                              numColumns={plate?.columns}
                              numRows={plate?.rows}
                              wells={wells}
                              onWellEnter={onWellEnter}
                              onWellLeave={onWellLeave}
                            />
                          )}
                        </PlateMapTooltip>
                      )}
                    />
                  }
                />
              </Stack>
            ) : (
              <Stack spacing={4}>
                <Box sx={{ alignSelf: 'start' }}>
                  <StepSelector
                    headerColor={TEAL_COLOR}
                    Header={
                      <Typography
                        variant="h6"
                        alignSelf="center"
                        sx={{ fontWeight: 'bold', color: 'text.primary' }}
                      >
                        Temperature
                      </Typography>
                    }
                    Body={
                      <StepInput
                        label="Set temperature"
                        value={action.temperature.value || ''}
                        onChange={e =>
                          handleUpdateIncubate(action.id, {
                            temperature: { selectedMode: 'const', value: e.target.value },
                          })
                        }
                        sx={{ minWidth: '200px' }}
                      />
                    }
                  />
                </Box>
                <Box sx={{ alignSelf: 'start' }}>
                  <StepSelector
                    headerColor={TEAL_COLOR}
                    Header={
                      <Typography
                        variant="h6"
                        alignSelf="center"
                        sx={{ fontWeight: 'bold', color: 'text.primary' }}
                      >
                        Time
                      </Typography>
                    }
                    Body={
                      <StepInput
                        label="Set duration"
                        value={action.duration.value || ''}
                        onChange={e =>
                          handleUpdateIncubate(action.id, {
                            duration: { selectedMode: 'const', value: e.target.value },
                          })
                        }
                        sx={{ minWidth: '200px' }}
                      />
                    }
                  />
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      ))}

      <Box sx={{ alignSelf: 'start' }}>
        <StepSelector
          headerColor={TEAL_COLOR}
          Header={
            <Typography
              variant="h6"
              alignSelf="center"
              sx={{ fontWeight: 'bold', color: 'text.primary' }}
            >
              New Action
            </Typography>
          }
          Body={
            <>
              <StepSelect
                value={newActionType}
                label="Select action type"
                options={[
                  { label: 'Liquid Handling', value: 'makeMixture' },
                  { label: 'Incubate', value: 'incubate' },
                ]}
                onChange={e => setNewActionType(e.target.value as ActionType)}
                sx={{ minWidth: '200px' }}
              />
              <LowProfileButton icon={<AddIcon />} onClick={handleAddAction}>
                <Typography noWrap>ADD</Typography>
              </LowProfileButton>
            </>
          }
        />
      </Box>
    </Stack>
  );
};

export default ActionsList;
